import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FacebookShareButton, FacebookIcon, LinkedinShareButton, LinkedinIcon, BlueskyIcon, BlueskyShareButton } from 'react-share';
import styles from './RecordPopup.module.css';
import { generateQuery } from '../CC/utils';
import { ccConfig } from '../CC/CC';
import { useCCContext } from '../CC/CCContext';
import Record from '../Record/Record';
import GenericIconButton from '../GenericComponents/GenericIconButton';
import * as ccUtils from '../CC/utils';
import genericStyles from '../GenericComponents/GenericStyles.module.css';
//import { html2pdf } from 'html3pdf'
declare var html3pdf: any;

interface RecordPopupProps {
  recordNumber: number;
  onClose: () => void;
}

function copyCanvasContent(sourceCanvas: HTMLCanvasElement, targetCanvas: HTMLCanvasElement) {
  const ctx = targetCanvas.getContext('2d');
  ctx?.drawImage(sourceCanvas, 0, 0);
}

const generatePdf = (objectNumber:string) => {

  if (window.osdViewer) {
    window.osdViewer.viewport.goHome();
  }
  setTimeout(() => {
    // Step 1: Create a deep clone of the original element.
    const originalElement = document.getElementById("pdf-container") as HTMLElement;
    const objectNumberElement = document.getElementById("objectnumber") as HTMLElement;
    const clonedElement = originalElement.cloneNode(true) as HTMLElement;

    // Copy canvas contents
    const originalCanvases = originalElement.querySelectorAll('canvas');
    const clonedCanvases = clonedElement.querySelectorAll('canvas');

    originalCanvases.forEach((canvas, index) => {
      copyCanvasContent(canvas, clonedCanvases[index] as HTMLCanvasElement);
    });

    clonedElement.style.top = '-10000px'; // move it far off-screen
    clonedElement.style.display = 'block'; // keep it hidden
    document.body.appendChild(clonedElement);

    setTimeout(() => {
      (clonedElement.querySelectorAll('.noFloatOnPdf') as NodeListOf<HTMLElement>).forEach(el => el.style.float = 'none');
      (clonedElement.querySelectorAll('.pdfOnly') as NodeListOf<HTMLElement>).forEach(el => el.style.display = 'block');
      (clonedElement.querySelectorAll('.screenOnly') as NodeListOf<HTMLElement>).forEach(el => el.style.display = 'none');
      (clonedElement.querySelectorAll('.pdfUnder') as NodeListOf<HTMLElement>).forEach(function(el) {
        el.style.display = 'inline-block'
      });
      (clonedElement.querySelectorAll('.pdfCenter') as NodeListOf<HTMLElement>).forEach(function(el) {
        el.style.width = '100%'
      });

      // Step 3: Send the modified cloned element to html3pdf.
      //(window as any).
      if (objectNumber === undefined) {
        //fieldname is added in record.tsx on each label from getDefaultRow()
        //this ensures we never have objectnumber is undefined
        objectNumber = objectNumberElement.innerHTML
      }
      const pdfConfig = {
        margin: 5,
        filename: 'ArtsEtMetiers_' + objectNumber + '_Notice.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        pagebreak: { mode: ['css', 'legacy'] },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        documentProperties: { title: objectNumber }
      };

      

      html3pdf().from(clonedElement).set(pdfConfig).toPdf().get('pdf').then((pdfObject:any) => {
        //var number_of_pages = pdfObject.internal.getNumberOfPages()
        var pdf_pages = pdfObject.internal.pages
        for (var i = 1; i < pdf_pages.length; i++) {
            pdfObject.setPage(i)
            pdfObject.setFontSize(7)
            pdfObject.text("Catalogue en ligne des Collections du musée des Arts et Métiers - https://collections.arts-et-metiers.net", 5, 5)
            pdfObject.text("page " + i + "/"+ (pdf_pages.length - 1) + " - " +  new Date().toLocaleDateString(), 5, 295)
        }
      }).save();
      //(window as any).worker;
      document.body.removeChild(clonedElement);
    });
  }, 1000);

}

const RecordPopup: React.FC<RecordPopupProps> = ({ recordNumber, onClose }) => {
  const { t } = useTranslation();
  const instanceRef = useRef({});
  //const { user, cc, setCc, sort, first, setFirst, recordsPerPage, setRecordsPerPage, viewMode, setViewMode, browseMode, setBrowseMode, data, setData } = useCCContext();
  const { user, cc, sort, setViewMode } = useCCContext();
  const [selectedRecord, setSelectedRecord] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(recordNumber);
  const [isInput, setIsInput] = useState(false);
  const [inputPage, setInputPage] = useState(currentPage);
  const [sliderPage, setSliderPage] = useState(currentPage); // Local copy for the slider

  const handleClose = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (e.target === e.currentTarget) {
      //console.log(viewMode);
      setViewMode('icon');
      onClose();
    }
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPage = parseInt(e.target.value, 10);
    setSliderPage(newPage); // Update the local copy only
  };

  const handleSliderMouseUp = () => {
    updateCurrentPage(sliderPage); // Update the actual value on mouse release
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputPage(parseInt(e.target.value, 10));
  };

  const handleBlur = () => {
    if (inputPage >= 1 && inputPage <= totalRecords) {
      updateCurrentPage(inputPage);
    }
    setIsInput(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleBlur();
    }
  };

  const updateCurrentPage = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const goToPreviousPage = () => updateCurrentPage(Math.max(1, Number(currentPage) - 1));
  const goToNextPage = () => updateCurrentPage(Math.min(totalRecords, Number(currentPage) + 1));
  const goToFirstPage = () => updateCurrentPage(1);
  const goToLastPage = () => updateCurrentPage(Number(totalRecords));

  useEffect(() => {
    const fetchData = async () => {
      const query = generateQuery(cc, user);
      const rangeStart = currentPage;

      const postData = {
        action: "get",
        command: "search",
        query: query,
        fields: "*",
        range: `${rangeStart}-${rangeStart}`,
        sort: ccConfig.sort[sort].field,
        responseformat: "json"
      };

      try {
        const data = await ccUtils.postWithCache(process.env.REACT_APP_CC_URL as string, postData, instanceRef);
        //console.log("payload", data);

        setTotalRecords(data.request.count);
        const records = Array.isArray(data?.records?.record) ? data?.records?.record : [data?.records?.record];
        setSelectedRecord(records[0] ?? null);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [currentPage]);

  function ShareButtons() {

    const invNo = selectedRecord?.data?.record?.objectnumber;
    const encodedInvNo = encodeURIComponent(invNo);
    const url = `${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}${window.location.pathname}?id=${encodedInvNo}`;
    const recordTitle = selectedRecord?.data?.record?.title?.title;
    let title = 'Musee des Arts et Métiers';
    if(recordTitle) {
      title = title + ' - ' + recordTitle;
    }

    return (
      <div className={styles.socialButtons}>
        <div className={styles.socialButtonWrapper}>
          <FacebookShareButton url={url} aria-label='Partager avec Facebook'>
            <FacebookIcon size={31} borderRadius={4} />
          </FacebookShareButton>
        </div>
        <div className={styles.socialButtonWrapper}>
          <LinkedinShareButton url={url} title={title} aria-label='Partager avec Linkedin'>
            <LinkedinIcon size={31} borderRadius={4} />
          </LinkedinShareButton>
        </div>
        <div className={styles.socialButtonWrapper}>
          <BlueskyShareButton url={url} title={title} aria-label='Partager avec Bluesky'>
            <BlueskyIcon size={31} borderRadius={4} />
          </BlueskyShareButton>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.popupoverlay} onClick={handleClose}>
      <div className={styles.RecordPopup} >
        <div className={styles.popupheader}>
          <div className={styles.Navigator} >
            <div className={styles.Paginator}>
              <GenericIconButton ariaLabel='Premier enregistrement' icon='light/angle-double-left.svg' width='22px' height='20px' onClick={() => goToFirstPage()} />
              <GenericIconButton ariaLabel='Enregistrement précédent' icon='light/angle-left.svg' width='22px' height='20px' onClick={() => goToPreviousPage()} />
              <GenericIconButton ariaLabel='Enregistrement suivant' icon='light/angle-right.svg' width='22px' height='20px' onClick={() => goToNextPage()} />
              <GenericIconButton ariaLabel='Dernier enregistrement' icon='light/angle-double-right.svg' width='22px' height='20px' onClick={() => goToLastPage()} />
            </div>
            <div className={styles.CurPage}>
              {t("curpage_page")}&nbsp;
              {isInput ? (
                <input
                  type="number"
                  value={inputPage}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyPress}
                  style={{ width: '50px' }}
                  autoFocus
                  aria-label="Saisir un numero d'enregistrement"
                />
              ) : (
                <span onClick={() => { setIsInput(true); setInputPage(currentPage); }}>{currentPage.toLocaleString()}</span>
              )}&nbsp;{t("curpage_of")} {Number(totalRecords).toLocaleString()}
            </div>
            <div className={styles.ResultSetSlider}>
              <input
                type="range"
                min="1"
                max={totalRecords}
                value={sliderPage} // Use the local copy for rendering
                onChange={handleSliderChange}
                onMouseUp={handleSliderMouseUp} // Handle mouse release
                onTouchEnd={handleSliderMouseUp}
                style={{ width: '100%' }}
                aria-label="Glisser pour choisir un enregistrement"
                />
            </div>
          </div>
          <div className={styles.closeButtonContainer}>
            <div className={styles.socials}>
              {ShareButtons()}
            </div>
            <div className={styles.generatePdfButton}>
              <button aria-label='Générer un fichier PDF' style={{backgroundColor: '#4F4F4F', color: 'white'}} className={genericStyles.GenericButton} onClick={() => generatePdf(selectedRecord?.data?.record?.objectnumber)}>{t("record_generate_pdf")}</button>
            </div>
            <div className={styles.closeButton}>
              <GenericIconButton icon='light/times.svg' width='18px' height='18px' onClick={() => onClose()} />
            </div>
          </div>
        </div>
        <div className={styles.popupcontent}>
          {isLoading ? (
            <div>Loading...</div>
          ) : (
            <Record record={selectedRecord} />
          )}
        </div>
      </div>
    </div>
  );
};

export default RecordPopup;
